<template>
    <div>
        <div class="row" id="campaigns-stats">
            <div class="col-md-12">
                <div class="campaign-box campaign-box--no_border">
                    <div class="campaign-details-chart-title">
                        <div class="campaign-details-subtitle">
                            Total Spent
                            <info-tooltip text="View your amount spent across all campaigns for the selected date range."></info-tooltip>
                        </div>
                    </div>

                    <div class="row campaign-stats-chart">
                        <div class="col-xs-12 campaign-metrics_div" v-cloak>
                            <div class="col-xs-12">
                                <div>
                                    <p>View your amount spent across all campaigns for the selected date range.</p>
                                </div>
                                <div>
                                    <span class="datepicker-label">Date range</span>

                                    <div class="row">
                                        <div class="col-sm-4">
                                            <looker-date-range-picker
                                                v-model="dateRange"
                                            ></looker-date-range-picker>
                                        </div>

                                        <div class="col-sm-2" style="display: flex; flex-direction: column">
                                            <h3 style="margin: 0">${{ totalDailySpend() | currency }}</h3>
                                            <small>Today's Spend</small>
                                        </div>

                                        <div class="col-sm-2" :class="{'text-muted': refreshing}" style="display: flex; flex-direction: column">
                                            <span v-show="loading" style="margin: 0">
                                                <content-loader></content-loader>
                                            </span>
                                            <h3 v-show="!loading" style="margin: 0">{{ (totalSpent) ? totalSpent : '-' }}</h3>
                                            <small>Total Spent</small>
                                        </div>

                                        <div class="col-sm-2" style="display: flex; flex-direction: column">
                                            <h3 dusk="account-balance" style="margin: 0">${{ accountBalance | currency }}</h3>
                                            <small>Account Balance</small>
                                        </div>

                                        <div v-if="embedUrl" v-show="!loading" class="col-sm-2" style="display: flex; flex-direction: column">
                                            <a @click.prevent="toggleLookerChart()" href="#" type="button" class="btn --gray" style="margin-top: 5px;">
                                                {{ showLookerChart ? 'Hide' : 'Show' }} Graph
                                            </a>
                                        </div>
                                    </div>

                                    <looker-embed v-if="embedUrl" v-show="showLookerChart"
                                                  :embedUrl="embedUrl"
                                                  :dateRangeString="dateRangeString"
                                    ></looker-embed>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <campaign-dashboard-list
            :create-campaign-url="createCampaignUrl"
            :loading="loading"
            :refreshingCampaignStats="refreshing"
            :campaigns="campaignsWithLookerData"
        ></campaign-dashboard-list>
    </div>
</template>

<script>
    import { slugify } from '../../utils.js'
    import dataDog from '../../mixins/datadog'
    import ContentLoader from 'js/components/utils/content-loader'
    import axios from 'axios'

    export default {
        mixins: [ dataDog ],

        props: ['embedUrl', 'vertical', 'campaigns', 'campaignSpends', 'campaignCounts'],

        data() {
            return {
                accountBalance:      null,
                loading:             true,
                refreshing:          true,
                totalSpent:          null,
                campaignsFromLooker: [],
                dateRange:           null,
                showLookerChart:     false
            }
        },

        watch: {
            dateRange() {
                this.refreshing = true

                Promise.all([
                    this.refreshTotalSpent(),
                    this.refreshCampaignDataFromLooker()
                ]).then(() => {
                    this.refreshing = false
                }).catch(() => {
                    this.refreshing = false
                })
            }
        },

        computed: {
            dateRangeString() {
                if (!this.dateRange) return null

                const adjustedEndDate = this.dateRange.end.clone().add(1, 'd')

                return `${this.dateRange.start.format('YYYY/MM/DD')} to ${adjustedEndDate.format('YYYY/MM/DD')}`
            },

            campaignsWithLookerData() {
                // if (this.loading) {
                //     // As weird as this looks, it needs to be here because the loading wheel is contained in another component.
                //     return []
                // }

                return _.map(this.campaigns, campaign => {
                    let lookerCampaign = _.find(this.campaignsFromLooker, { campaign_id: Number(campaign.id) })
                    let sourceCampaign = Object.assign({}, campaign)

                    if (this.campaignsFromLooker) {
                        sourceCampaign.cost = lookerCampaign ? lookerCampaign.amount_spent : 0
                        sourceCampaign.daily_spend = this.campaignSpend(campaign.id)
                        sourceCampaign.click_count = lookerCampaign && lookerCampaign.actions
                            ? lookerCampaign.actions
                            : (this.campaignCounts[campaign.id] || 0)
                    } else {
                        sourceCampaign.cost = null
                        sourceCampaign.daily_spend = this.campaignSpend(campaign.id)
                        sourceCampaign.click_count = '-'
                    }

                    return sourceCampaign
                })
            },

            createCampaignUrl() {
                return route('campaigns.create', { productCategorySlug: slugify(this.vertical) })
            },

            hasLookerInformation() {
                return this.campaignsFromLooker.length > 0
            }
        },

        methods: {
            fetchAccountBalance() {
                this.$http.get(route('api.advertiser.account-balance.show', {
                    'vertical': this.vertical
                })).then(response => {
                    this.accountBalance = response.data.account_balance
                })
            },

            refreshCampaignDataFromLooker() {
                return this.$http.get(route('api.looker.campaign-data', {
                    'msg_date_date': this.dateRangeString,
                    'vertical':      this.vertical
                })).then(response => {
                    this.campaignsFromLooker = response.body.campaigns
                })
            },

            refreshTotalSpent() {
                return this.$http.get(route('api.looker.total-spent', {
                    'msg_date_date': this.dateRangeString,
                    'vertical':      this.vertical
                })).then(response => {
                    this.totalSpent = response.body.total_spent
                })
            },

            campaignSpend(id) {
                return (Number(this.campaignSpends[id]) || 0) / 100
            },

            totalDailySpend() {
                return (Object.values(this.campaignSpends).reduce((a, v) => Number(a) + Number(v), 0)) / 100
            },

            toggleLookerChart() {
                this.showLookerChart = !this.showLookerChart
                this.dataDogIncrement((this.showLookerChart ? 'show' : 'hide') + '_looker_chart_dashboard')
                localStorage.setItem('show-looker-chart-dashboard', this.showLookerChart)
            }
        },

        created() {
            this.showLookerChart = localStorage.getItem('show-looker-chart-dashboard') === 'true'
        },

        // Note: this has to be "mounted" and not "created", because
        // dateRangeString needs time to be updated from the picker component.
        mounted() {
            Promise.all([
                this.refreshTotalSpent(),
                this.fetchAccountBalance(),
                this.refreshCampaignDataFromLooker()
            ]).then(() => {
                this.loading = false
            }).catch(() => {
                this.loading = false
            })
        },

        components: {
            ContentLoader
        }
    }
</script>
